import React, { useEffect, useMemo, useState } from "react";
import { Gift, Pencil, Plus, RefreshCw, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

const emptyForm = {
  name: "",
  trigger_type: "every_deposit",
  min_amount: "0",
  max_amount: "0",
  bonus_percent: "5",
  max_bonus: "0",
  start_date: "",
  end_date: "",
  is_active: true,
};

function AdminBonus() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);

  // Welcome bonus settings
  const [wbEnabled, setWbEnabled] = useState(false);
  const [wbAmount, setWbAmount] = useState("0");
  const [wbSaving, setWbSaving] = useState(false);
  const [wbMsg, setWbMsg] = useState("");

  const activeCount = items.filter((item) => item.is_active !== 0).length;
  const totalCredited = items.reduce((sum, item) => sum + Number(item.credited || 0), 0);
  const recipients = items.reduce((sum, item) => sum + Number(item.recipients || 0), 0);

  const loadPromotions = async () => {
    try {
      const res = await API.get("/admin/promotions");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      setItems([]);
    }
  };

  const loadWelcomeBonus = async () => {
    try {
      const res = await API.get("/admin/settings");
      setWbEnabled(String(res.data?.signup_bonus_enabled || "0") === "1");
      setWbAmount(String(res.data?.signup_bonus || "0"));
    } catch {}
  };

  const saveWelcomeBonus = async () => {
    setWbSaving(true);
    setWbMsg("");
    try {
      await API.post("/admin/settings", {
        signup_bonus_enabled: wbEnabled ? "1" : "0",
        signup_bonus: wbAmount,
      });
      setWbMsg("Saved!");
      setTimeout(() => setWbMsg(""), 2000);
    } catch {
      setWbMsg("Error saving");
    } finally {
      setWbSaving(false);
    }
  };

  useEffect(() => {
    loadPromotions();
    loadWelcomeBonus();
  }, []);

  const savePromotion = async (event) => {
    event.preventDefault();
    if (editing?.id) {
      await API.put(`/admin/promotions/${editing.id}`, form);
    } else {
      await API.post("/admin/promotions", form);
    }
    setEditing(null);
    setForm(emptyForm);
    await loadPromotions();
  };

  const editPromotion = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      trigger_type: item.trigger_type || "every_deposit",
      min_amount: String(item.min_amount ?? 0),
      max_amount: String(item.max_amount ?? 0),
      bonus_percent: String(item.bonus_percent ?? 0),
      max_bonus: String(item.max_bonus ?? 0),
      start_date: String(item.start_date || "").slice(0, 10),
      end_date: String(item.end_date || "").slice(0, 10),
      is_active: item.is_active !== 0,
    });
  };

  const deletePromotion = async (id) => {
    await API.delete(`/admin/promotions/${id}`);
    await loadPromotions();
  };

  const formTitle = useMemo(() => editing ? "Edit Bonus Program" : "Create Bonus Program", [editing]);

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h1 style={title}>Promotions & Bonuses</h1>
            <p style={subtitle}>Create first-deposit and every-deposit bonus slabs with different percentages.</p>
          </div>
          <button type="button" style={refreshButton} onClick={loadPromotions}><RefreshCw size={16} /></button>
        </div>

        <div style={summaryGrid}>
          <Summary label="Total Programs" value={items.length} />
          <Summary label="Active" value={activeCount} />
          <Summary label="Recipients" value={recipients} />
          <Summary label="Credited" value={formatMoney(totalCredited)} />
        </div>

        {/* ── Welcome Bonus ─────────────────────────────────────────────── */}
        <div style={wbCard}>
          <div style={wbHeader}>
            <div>
              <h3 style={wbTitle}>🎁 Welcome Bonus</h3>
              <p style={wbSub}>Flat bonus credited automatically to every new user right after registration.</p>
            </div>
            <button
              type="button"
              style={wbToggleBtn(wbEnabled)}
              onClick={async () => {
                const next = !wbEnabled;
                setWbEnabled(next);
                try {
                  await API.post("/admin/settings", { signup_bonus_enabled: next ? "1" : "0" });
                  setWbMsg(next ? "Enabled!" : "Disabled");
                  setTimeout(() => setWbMsg(""), 2000);
                } catch { setWbMsg("Save failed"); }
              }}
            >
              {wbEnabled
                ? <><ToggleRight size={22} /> ON</>
                : <><ToggleLeft size={22} /> OFF</>}
            </button>
          </div>
          <div style={wbRow}>
            <label style={wbField}>
              <span>Bonus Amount (₹)</span>
              <input
                type="number"
                min="0"
                step="1"
                style={wbInput}
                value={wbAmount}
                onChange={(e) => setWbAmount(e.target.value)}
                placeholder="e.g. 50"
              />
            </label>
            <button type="button" style={wbSaveBtn} onClick={saveWelcomeBonus} disabled={wbSaving}>
              {wbSaving ? "Saving…" : "Save"}
            </button>
            {wbMsg && <span style={wbMsgStyle(wbMsg)}>{wbMsg}</span>}
          </div>
          {wbEnabled && Number(wbAmount) > 0 && (
            <p style={wbNote}>
              ✅ Active — every new signup will receive <strong>₹{Number(wbAmount).toLocaleString("en-IN")}</strong> automatically within ~5 seconds of registering.
            </p>
          )}
          {(!wbEnabled || !(Number(wbAmount) > 0)) && (
            <p style={wbNoteOff}>⏸ Disabled — new users will NOT receive a welcome bonus until this is turned on with an amount &gt; 0.</p>
          )}
        </div>

        <form style={formCard} onSubmit={savePromotion}>
          <h3 style={cardTitle}>{formTitle}</h3>
          <div style={formGrid}>
            <Field label="Program Name" value={form.name} type="text" onChange={(value) => setForm((c) => ({ ...c, name: value }))} placeholder="FIRST_DEPOSIT_10%" />
            <label style={field}>
              <span>Bonus Type</span>
              <select style={input} value={form.trigger_type} onChange={(e) => setForm((c) => ({ ...c, trigger_type: e.target.value }))}>
                <option value="first_deposit">First Deposit</option>
                <option value="every_deposit">Every Deposit</option>
              </select>
            </label>
            <Field label="Min Amount" value={form.min_amount} onChange={(value) => setForm((c) => ({ ...c, min_amount: value }))} />
            <Field label="Max Amount (0 = no max)" value={form.max_amount} onChange={(value) => setForm((c) => ({ ...c, max_amount: value }))} />
            <Field label="Bonus %" value={form.bonus_percent} onChange={(value) => setForm((c) => ({ ...c, bonus_percent: value }))} />
            <Field label="Max Bonus (0 = no cap)" value={form.max_bonus} onChange={(value) => setForm((c) => ({ ...c, max_bonus: value }))} />
            <Field label="Start Date" type="date" value={form.start_date} onChange={(value) => setForm((c) => ({ ...c, start_date: value }))} />
            <Field label="End Date" type="date" value={form.end_date} onChange={(value) => setForm((c) => ({ ...c, end_date: value }))} />
          </div>
          <label style={checkRow}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((c) => ({ ...c, is_active: e.target.checked }))} />
            Active and auto-credit on deposit approval
          </label>
          <div style={actionRow}>
            <button type="button" style={ghostButton} onClick={() => { setEditing(null); setForm(emptyForm); }}>Clear</button>
            <button type="submit" style={primaryButton}><Plus size={16} /> {editing ? "Update Program" : "Create Promotion"}</button>
          </div>
        </form>

        <section style={tableCard}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Promotion</th>
                <th style={th}>Type</th>
                <th style={th}>Slab</th>
                <th style={th}>Bonus</th>
                <th style={th}>Duration</th>
                <th style={th}>Recipients</th>
                <th style={th}>Credited</th>
                <th style={th}>Status</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={td}>
                    <div style={promoCell}><span style={promoIcon}><Gift size={16} /></span><strong>{item.name}</strong></div>
                  </td>
                  <td style={td}>{item.trigger_type === "first_deposit" ? "First Deposit" : "Every Deposit"}</td>
                  <td style={td}>{formatMoney(item.min_amount)} to {Number(item.max_amount || 0) ? formatMoney(item.max_amount) : "No max"}</td>
                  <td style={td}><strong>{Number(item.bonus_percent || 0)}%</strong><small style={subText}>Max {Number(item.max_bonus || 0) ? formatMoney(item.max_bonus) : "No cap"}</small></td>
                  <td style={td}>{formatDate(item.start_date)} to {formatDate(item.end_date)}</td>
                  <td style={td}>{Number(item.recipients || 0)}</td>
                  <td style={td}><strong style={greenText}>{formatMoney(item.credited)}</strong></td>
                  <td style={td}><span style={item.is_active !== 0 ? activeBadge : draftBadge}>{item.is_active !== 0 ? "Active" : "Draft"}</span></td>
                  <td style={td}>
                    <button type="button" style={editButton} onClick={() => editPromotion(item)}><Pencil size={15} /></button>
                    <button type="button" style={deleteButton} onClick={() => deletePromotion(item.id)}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
              {!items.length && <tr><td style={emptyCell} colSpan={9}>No bonus programs yet</td></tr>}
            </tbody>
          </table>
        </section>
      </section>
    </AdminLayout>
  );
}

function Field({ label, value, onChange, type = "number", placeholder = "" }) {
  return (
    <label style={field}>
      <span>{label}</span>
      <input type={type} style={input} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function Summary({ label, value }) {
  return <article style={summaryCard}><span>{label}</span><strong>{value}</strong></article>;
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toFixed(0)}`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const page = { display: "grid", gap: "18px" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const title = { margin: 0, fontSize: 22, fontWeight: 800, color: "#1e1b4b" };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: "13px" };
const refreshButton = { width: "44px", height: "44px", border: "1px solid #dbe4f0", background: "#fff", borderRadius: "12px", cursor: "pointer", display: "grid", placeItems: "center" };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "14px" };
const summaryCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "18px", display: "grid", gap: "8px" };
const formCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", padding: "18px", display: "grid", gap: "14px" };
const cardTitle = { margin: 0, fontSize: "16px", fontWeight: 900 };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "12px" };
const field = { display: "grid", gap: "7px", color: "#334155", fontSize: "12px", fontWeight: 800 };
const input = { height: "44px", border: "1px solid #cbd5e1", borderRadius: "10px", padding: "0 12px", fontSize: "14px", background: "#fff" };
const checkRow = { display: "flex", alignItems: "center", gap: "8px", color: "#334155", fontWeight: 700 };
const actionRow = { display: "grid", gridTemplateColumns: "180px 1fr", gap: "12px" };
const ghostButton = { height: "44px", border: "1px solid #cbd5e1", background: "#fff", borderRadius: "10px", fontWeight: 800, cursor: "pointer" };
const primaryButton = { height: "44px", border: 0, background: "linear-gradient(135deg, #f97316, #ea580c)", color: "#fff", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontWeight: 900, cursor: "pointer" };
const tableCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", overflow: "auto" };
const table = { width: "100%", minWidth: "1100px", borderCollapse: "collapse" };
const th = { height: "48px", padding: "0 18px", textAlign: "left", color: "#64748b", background: "#f8fafc", fontSize: "11px", textTransform: "uppercase" };
const td = { padding: "14px 18px", borderTop: "1px solid #f1f5f9", color: "#0f172a", fontSize: "13px" };
const promoCell = { display: "flex", alignItems: "center", gap: "10px" };
const promoIcon = { width: "34px", height: "34px", display: "grid", placeItems: "center", background: "#ede9fe", color: "#7c3aed" };
const subText = { display: "block", color: "#64748b", marginTop: "4px" };
const greenText = { color: "#059669" };
const activeBadge = { padding: "6px 10px", background: "#dcfce7", color: "#15803d", fontWeight: 900 };
const draftBadge = { ...activeBadge, background: "#f1f5f9", color: "#64748b" };
const editButton = { width: "34px", height: "34px", border: 0, background: "#dbeafe", color: "#2563eb", cursor: "pointer", marginRight: "6px" };
const deleteButton = { ...editButton, background: "#fee2e2", color: "#dc2626" };
const emptyCell = { padding: "36px", textAlign: "center", color: "#94a3b8" };

// Welcome bonus styles
const wbCard       = { background: "#fff", border: "2px solid #f97316", borderRadius: 18, padding: "18px 20px", display: "grid", gap: 12 };
const wbHeader     = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 };
const wbTitle      = { margin: 0, fontSize: 16, fontWeight: 900, color: "#1e1b4b" };
const wbSub        = { margin: "4px 0 0", color: "#64748b", fontSize: 12 };
const wbToggleBtn  = (on) => ({ height: 38, padding: "0 16px", border: 0, borderRadius: 10, background: on ? "#16a34a" : "#94a3b8", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" });
const wbRow        = { display: "flex", alignItems: "flex-end", gap: 12 };
const wbField      = { display: "grid", gap: 6, color: "#334155", fontSize: 12, fontWeight: 800, flex: "0 0 180px" };
const wbInput      = { height: 44, border: "1px solid #cbd5e1", borderRadius: 10, padding: "0 12px", fontSize: 16, fontWeight: 700, background: "#fff", width: "100%", boxSizing: "border-box" };
const wbSaveBtn    = { height: 44, padding: "0 24px", border: 0, borderRadius: 10, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" };
const wbMsgStyle   = (msg) => ({ fontSize: 13, fontWeight: 700, color: msg === "Saved!" ? "#16a34a" : "#dc2626", alignSelf: "center" });
const wbNote       = { margin: 0, fontSize: 13, color: "#15803d", background: "#dcfce7", borderRadius: 8, padding: "8px 12px" };
const wbNoteOff    = { margin: 0, fontSize: 13, color: "#92400e", background: "#fef3c7", borderRadius: 8, padding: "8px 12px" };

export default AdminBonus;
